import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlayCircle, faPauseCircle } from '@fortawesome/free-regular-svg-icons';
import { faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import animateScrollTo from 'animated-scroll-to';
import exact from 'prop-types-exact';
import Button from '../../../common/button/Button';
import MusicSingleVideosSlider from '../containers/MusicSingleVideosSliderContainer';
import MusicSingleLyrics from './MusicSingleLyrics';
import ErrorPage from '../../../common/errorPage/ErrorPage';
import Spinner from '../../../common/Spinner/Spinner';
import HeaderTags from '../../../common/headerTags/HeaderTags';

export default class MusicSinglePage extends Component {
    componentDidMount() {
        this._scrollToTop();

        if (!this.props.notFound) {
            if (!this.props.track || !this.props.artist) {
                return this.props.setNotFound(true);
            }

            this.props.getSingleMusicDataIfNeeded(this.props.artist, this.props.track);
        }

        return false;
    }

    shouldComponentUpdate(nextProps) {
        return nextProps.news !== this.props.news
            || nextProps.breakpoint !== this.props.breakpoint
            || nextProps.playerStatus !== this.props.playerStatus
            || nextProps.currentSources !== this.props.currentSources
            || nextProps.notFound !== this.props.notFound
            || nextProps.isRelatedNews !== this.props.isRelatedNews
            || nextProps.isLoading !== this.props.isLoading
            || nextProps.location.pathname !== this.props.location.pathname;
    }

    componentDidUpdate(prevProps) {
        // when clicking the music page link on aovivoBar and the user is already on a music page, the content won't update if the whole block below doesn't validate and compare the slugs of the previous music with the current slugs passed on the url.
        const {
            artistSlug,
            musicSlug,
        } = prevProps.singleMusicData;
        const fullMusicSlug = this.props.location.pathname.split('/musicas/')[1];

        if (!fullMusicSlug) {
            // this should never happen as the route is treated differently in the server
            return this.props.setNotFound(true);
        }

        const [track, artist] = fullMusicSlug.split('+');

        if (!track || !artist) {
            return this.props.setNotFound(true);
        }

        if (track !== musicSlug || artist !== artistSlug) {
            this.props.getSingleMusicDataIfNeeded(artist, track);

            return this._scrollToTop();
        }

        return true;
    }

    componentWillUnmount() {
        this.props.setNotFound(false); // unset 404 status when leaving
    }

    _handlePlay = () => {
        const src = this.props.singleMusicData.musicTrack;
        const playerStatus = this.props.playerStatus;
        const currentMusicSrc = this.props.currentSources[0]?.src;

        if ((playerStatus === 'paused') && (currentMusicSrc === src)) {
            return this.props.playMediaPlayer();
        }

        // defining cover img
        const albumCover = this.props.singleMusicData.albumCover || 'defaultAlbum.jpg';
        const coverImage = `https://a1media.antena1.com.br/image/album/1000x1000/${albumCover}`;
        const meta = {
            album: this.props.singleMusicData.albumName,
            artist: this.props.singleMusicData.artistName,
            cover: coverImage,
            song: this.props.singleMusicData.musicName,
        };
        const srcWithType = {
            src,
            type: 'audio/mp3',
        };

        this._setPlaylist();

        return this.props.playMediaFromSourceWithMeta(srcWithType, meta);
    }

    _handlePause = () => {
        this.props.pauseMediaPlayer();
    }

    // eslint-disable-next-line class-methods-use-this
    _scrollToTop = () => {
        animateScrollTo(0, { speed: 700 });
    }

    _setPlaylist = () => {
        const playlistType = 'musicSingle';
        const musicIndexInPlaylist = 0;
        const {
            musicId,
            musicTrack,
            albumCover,
            iTunesLink,
            artistName,
            musicName,
            musicSlug,
            artistSlug,
            albumName,
        } = this.props.singleMusicData;
        const trackList = [{
            musicId,
            musicTrack,
            albumCover,
            iTunesLink,
            artistName,
            musicName,
            musicSlug,
            artistSlug,
            albumName,
        }];

        this.props.setPlaylist(playlistType, trackList, musicIndexInPlaylist, musicId);
    }

    _mountVideos() {
        return (
            <div className="musicsSinglePageLyricVideo">
                <h2>Video</h2>
                <MusicSingleVideosSlider
                    musicVideoYoutubeId={this.props.singleMusicData.musicVideoYoutubeId}
                    musicVideoFileName={this.props.singleMusicData.musicVideoFileName}
                    lyricsVideoYoutubeId={this.props.singleMusicData.lyricsVideoYoutubeId}
                    lyricsVideoFileName={this.props.singleMusicData.lyricsVideoFileName}
                />

            </div>
        );
    }

    _mountLyrics() {
        const breakpoint = this.props.breakpoint;
        const isMobile = breakpoint.isSmall || breakpoint.isExtraSmall;

        return (
            <div className="musicSingleLyrics">
                <h2 className="tw-font-bold tw-mb-7 tw-text-3xl tw-text-black">Letra</h2>
                <MusicSingleLyrics
                    isMobile={isMobile}
                    musicLyrics={this.props.singleMusicData.musicLyrics}
                    musicTranslation={this.props.singleMusicData.musicTranslation}
                />
            </div>
        );
    }

    render() {
        if (this.props.notFound) {
            return <ErrorPage
                errorNumber={404}
                backLink="/musicas"
                title="NÃO ACHAMOS ESSA MÚSICA :("
                message="Verifique se o link foi digitado corretamente."
                buttonText="Clique aqui para ver todas as músicas"
            />;
        }

        const {
            artistName,
            artistSlug,
            musicName,
            albumName,
            albumRelease,
            albumCover,
            musicTrack,
            musicVideoYoutubeId,
            lyricsVideoYoutubeId,
        } = this.props.singleMusicData;
        let imgSize = '300x300';
        const breakpoint = this.props.breakpoint;

        if (breakpoint.isSmall || breakpoint.isExtraSmall) {
            imgSize = '200x200';
        }

        const albumImgLink = `https://a1media.antena1.com.br/image/album/${imgSize}/`;
        const imageCover = albumCover || 'defaultAlbum.jpg';
        const isCurrentMusic = this.props.isLive === 'otherMedia' && musicName === this.props.playerSong && artistName === this.props.playerArtist;
        const isPlaying = isCurrentMusic && this.props.playerStatus === 'playing';
        const isLoading = isCurrentMusic && this.props.playerStatus === 'loading';
        let musicSinglePageThumbClass = 'musicSinglePageThumb';

        musicSinglePageThumbClass += !musicTrack ? ' notPlayable' : '';

        const pageTitle = artistName && musicName ? `${musicName} - ${artistName}` : 'Música';
        const headerTags = {
            title: `${pageTitle} - Letra, tradução e clipe`,
            meta: {
                description: `Ouça, veja a letra e a tradução de ${pageTitle} na Antena 1!`,
                'og:title': pageTitle,
                'og:description': `Ouça, veja a letra e a tradução de ${pageTitle} na Antena 1!`,
                'og:image': `${albumImgLink}${imageCover}`,
            },
        };

        return (
            <React.Fragment>
                <HeaderTags title={headerTags.title} meta={headerTags.meta} />
                <div className="musicsSingleSection">
                    <Button className="backToPage" title="Veja outras músicas." onClick={() => this.props.history.goBack()}>
                        <img src="/keyboard-right-arrow-button.svg" alt="Ícone seta para a esquerda" />
                        <span>Veja todas as músicas.</span>
                    </Button>
                    {
                        !this.props.isLoading ?
                            <div>
                                <header>
                                    <h1>
                                        {`${artistName} - ${musicName}`}
                                    </h1>
                                </header>
                                <div className="musicSinglePageIntro">
                                    <div className="musicSinglePageArt">
                                        {/* @TODO add the following button a key event on accessibility branch */}
                                        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                                        <div
                                            role="button"
                                            tabIndex="0"
                                            className={musicSinglePageThumbClass}
                                            onClick={
                                                musicTrack &&
                                                (isPlaying || isLoading
                                                    ? this._handlePause
                                                    : this._handlePlay
                                                )
                                            }
                                        >
                                            <img key="musicSinglePageThumbImg" className="musicSinglePageThumbImg" src={albumImgLink + imageCover} alt="" />
                                            {
                                                musicTrack &&
                                                <div key="imageOverlay" className={`imageOverlay ${!musicTrack ? ' notPlayable' : ''}`}>
                                                    {
                                                        isLoading
                                                            ? <FontAwesomeIcon icon={faSyncAlt} className="rotating" />
                                                            : isPlaying
                                                                ? <FontAwesomeIcon icon={faPauseCircle} />
                                                                : <FontAwesomeIcon icon={faPlayCircle} />
                                                    }
                                                </div>
                                            }
                                        </div>
                                    </div>
                                    <div className="musicSinglePageInfo">
                                        {
                                            artistName &&
                                            <p className="singleInfo">
                                                <span>Artista: </span>
                                                <Button title={`Saiba mais sobre ${artistName}`} to={`/artistas/${artistSlug}`}>
                                                    {artistName}
                                                </Button>
                                            </p>
                                        }
                                        {
                                            albumName &&
                                            <p className="singleInfo">
                                                <span>Album: </span>
                                                {albumName}
                                            </p>
                                        }
                                        {
                                            albumRelease &&
                                            <p className="singleInfo">
                                                <span>Data de lançamento: </span>
                                                {albumRelease}
                                            </p>
                                        }
                                    </div>
                                </div>
                                {(musicVideoYoutubeId || lyricsVideoYoutubeId) && this._mountVideos()}
                                {this._mountLyrics()}
                                <div className="noContentBlock">
                                    <p className="noContentWarning">
                                        Achou algo de errado na página?
                                        <br />
                                        Entre em
                                        {' '}
                                        <Button title="Entre em contato" to="/contato" className="tw-font-bold tw-underline">contato</Button>
                                        {' '}
                                        para nos notificar!
                                    </p>
                                </div>
                            </div>
                            : <Spinner />
                    }
                </div>
            </React.Fragment>
        );
    }
}

MusicSinglePage.propTypes = exact({
    getSingleMusicDataIfNeeded: PropTypes.func.isRequired,
    artist: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
    ]),
    track: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
    ]),
    singleMusicData: PropTypes.object.isRequired,
    news: PropTypes.array,
    isRelatedNews: PropTypes.bool.isRequired,
    currentSources: PropTypes.arrayOf(
        PropTypes.shape({
            src: PropTypes.string.isRequired,
            type: PropTypes.string,
        }),
    ),
    isLive: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.string,
    ]).isRequired,
    playerStatus: PropTypes.string.isRequired,
    playMediaPlayer: PropTypes.func.isRequired,
    pauseMediaPlayer: PropTypes.func.isRequired,
    playMediaFromSourceWithMeta: PropTypes.func.isRequired,
    playerArtist: PropTypes.string,
    playerSong: PropTypes.string,
    setPlaylist: PropTypes.func,
    isLoading: PropTypes.bool.isRequired,
    breakpoint: PropTypes.object.isRequired,
    setNotFound: PropTypes.func.isRequired,
    notFound: PropTypes.bool.isRequired,
    location: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    /* eslint-disable react/no-unused-prop-types */
    // Injected through withRouter but are not used
    match: PropTypes.object,
    staticContext: PropTypes.object,
    /* eslint-enable react/no-unused-prop-types */

});
